import { supabase } from '../supabase';

/**
 * Log an audit action to the audit_logs table
 * Use this for actions that aren't automatically logged by database triggers
 *
 * @param {string} action - Action performed (e.g., 'approve_user', 'assign_coach')
 * @param {string} resourceType - Type of resource (e.g., 'user', 'program', 'exercise')
 * @param {number} resourceId - ID of the affected resource
 * @param {object} oldValues - Previous state (for updates/deletes)
 * @param {object} newValues - New state (for creates/updates)
 * @returns {Promise<void>}
 */
export const logAuditAction = async (action, resourceType, resourceId, oldValues = null, newValues = null) => {
  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.warn('Cannot log audit action: No authenticated user');
      return;
    }

    // Get user ID from users table
    const { data: userData } = await supabase
      .from('users')
      .select('id')
      .eq('auth_user_id', user.id)
      .single();

    if (!userData) {
      console.warn('Cannot log audit action: User not found in users table');
      return;
    }

    // Insert audit log
    const { error } = await supabase
      .from('audit_logs')
      .insert({
        user_id: userData.id,
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        old_values: oldValues,
        new_values: newValues,
        ip_address: null, // Can be captured if needed
        user_agent: navigator.userAgent
      });

    if (error) {
      console.error('Error logging audit action:', error);
    }
  } catch (err) {
    console.error('Error in logAuditAction:', err);
  }
};

/**
 * Pre-defined audit action types for consistency
 */
export const AUDIT_ACTIONS = {
  // User actions
  CREATE_USER: 'create_user',
  UPDATE_USER: 'update_user',
  DELETE_USER: 'delete_user',
  APPROVE_USER: 'approve_user',
  REJECT_USER: 'reject_user',
  SUSPEND_USER: 'suspend_user',
  REACTIVATE_USER: 'reactivate_user',
  ARCHIVE_USER: 'archive_user',
  RESTORE_USER: 'restore_user',
  ASSIGN_COACH: 'assign_coach',
  CHANGE_ROLE: 'change_role',
  GRANT_COMPLIMENTARY: 'grant_complimentary',
  REVOKE_COMPLIMENTARY: 'revoke_complimentary',

  // Program actions
  CREATE_PROGRAM: 'create_program',
  UPDATE_PROGRAM: 'update_program',
  DELETE_PROGRAM: 'delete_program',
  ASSIGN_PROGRAM: 'assign_program',
  ARCHIVE_PROGRAM: 'archive_program',

  // Exercise actions
  CREATE_EXERCISE: 'create_exercise',
  UPDATE_EXERCISE: 'update_exercise',
  DELETE_EXERCISE: 'delete_exercise',

  // System actions
  LOGIN: 'login',
  LOGOUT: 'logout',
  PASSWORD_RESET: 'password_reset',
  SETTINGS_CHANGE: 'settings_change',

  // Deletion request actions
  APPROVE_DELETION_REQUEST: 'approve_deletion_request',
  DISMISS_DELETION_REQUEST: 'dismiss_deletion_request'
};

/**
 * Resource types for consistency
 */
export const RESOURCE_TYPES = {
  USER: 'user',
  PROGRAM: 'program',
  EXERCISE: 'exercise',
  WORKOUT: 'workout',
  CHECK_IN: 'check_in',
  SYSTEM: 'system',
  DELETION_REQUEST: 'deletion_request'
};

/**
 * Helper function to log user approval
 */
export const logUserApproval = async (userId, userName) => {
  await logAuditAction(
    AUDIT_ACTIONS.APPROVE_USER,
    RESOURCE_TYPES.USER,
    userId,
    { status: 'pending' },
    { status: 'active', userName }
  );
};

/**
 * Helper function to log user rejection
 */
export const logUserRejection = async (userId, userName) => {
  await logAuditAction(
    AUDIT_ACTIONS.REJECT_USER,
    RESOURCE_TYPES.USER,
    userId,
    { status: 'pending' },
    { status: 'rejected', userName }
  );
};

/**
 * Helper function to log a user being archived (moved to the Inactive list)
 */
export const logUserArchive = async (userId, userName, reason) => {
  await logAuditAction(
    AUDIT_ACTIONS.ARCHIVE_USER,
    RESOURCE_TYPES.USER,
    userId,
    { archived: false },
    { archived: true, reason, userName }
  );
};

/**
 * Helper function to log a user being restored from the Inactive list
 */
export const logUserRestore = async (userId, userName) => {
  await logAuditAction(
    AUDIT_ACTIONS.RESTORE_USER,
    RESOURCE_TYPES.USER,
    userId,
    { archived: true },
    { archived: false, userName }
  );
};

/**
 * Helper function to log coach assignment
 */
export const logCoachAssignment = async (clientId, clientName, coachId, coachName) => {
  await logAuditAction(
    AUDIT_ACTIONS.ASSIGN_COACH,
    RESOURCE_TYPES.USER,
    clientId,
    { coach_id: null },
    { coach_id: coachId, clientName, coachName }
  );
};

/**
 * Helper function to log role change
 */
export const logRoleChange = async (userId, userName, oldRole, newRole) => {
  await logAuditAction(
    AUDIT_ACTIONS.CHANGE_ROLE,
    RESOURCE_TYPES.USER,
    userId,
    { role: oldRole },
    { role: newRole, userName }
  );
};

/**
 * Helper function to log user suspension
 */
export const logUserSuspension = async (userId, userName) => {
  await logAuditAction(
    AUDIT_ACTIONS.SUSPEND_USER,
    RESOURCE_TYPES.USER,
    userId,
    { status: 'active' },
    { status: 'suspended', userName }
  );
};

/**
 * Helper function to log user reactivation
 */
export const logUserReactivation = async (userId, userName) => {
  await logAuditAction(
    AUDIT_ACTIONS.REACTIVATE_USER,
    RESOURCE_TYPES.USER,
    userId,
    { status: 'suspended' },
    { status: 'active', userName }
  );
};
